import * as React from 'react';
import { useEffect, useRef } from 'react';
import { ErrorBoundary } from '@sentry/react';
import { cn } from '../../../utils/ClassNamesUtils';
import { OSCAR_CONTENT_INSET, OSCAR_READING_MEASURE } from '../../constants/AIAgentConstants';
import { AgentUnsentMessages } from '../../../components/agent_chat/useAgentChat';
import { AgentActionOutcome, AgentTranscriptEntry } from '../../../types/agentHarness';
import OscarFileCard from '../shared/OscarFileCard';
import OscarTeamRequestSentCard from '../shared/OscarTeamRequestSentCard';
import OscarMarkdown from '../shared/OscarMarkdown';
import OscarUnsentMessage from '../shared/OscarUnsentMessage';
import { useOscarTurnStatus } from '../../hooks/useOscarTurnStatus';
import { humanizeAgentError } from '../../../utils/AgentErrorUtils';
import OscarActivityTrace, { OscarActivityStep } from './OscarActivityTrace';
import OscarApprovalQueue from '../oscar_question_cards/OscarApprovalQueue';
import OscarActionReviewCard from '../oscar_question_cards/OscarActionReviewCard';

type ToolEntry = Extract<AgentTranscriptEntry, { kind: 'tool' }>;
type ApprovalEntry = Extract<AgentTranscriptEntry, { kind: 'approval' }>;

type TranscriptGroup =
	| { kind: 'entry'; id: string; entry: AgentTranscriptEntry }
	| {
			kind: 'activity';
			id: string;
			/** Id of the user message that started the turn. */
			turnId: string;
			steps: OscarActivityStep[];
			/** The turn's last activity row — the one that carries its summary. */
			lastInTurn: boolean;
			/** The turn ended on a failed tool call, with no answer after it. */
			failed: boolean;
			/** Tool calls across the whole turn; set on its last row. */
			turnStepCount: number;
			/** The harness retried the turn on a stronger model; set on its last row. */
			turnRetried: boolean;
	  }
	| { kind: 'approvals'; id: string; approvals: ApprovalEntry[] };

/**
 * A user message starts a turn; the tool calls, approvals and answer after it
 * belong to that turn. Everything used to sit at the same `gap-s`, so a
 * conversation was one undifferentiated stack with no way to see where a
 * question ended and the next began.
 */
const startsTurn = (group: TranscriptGroup, index: number): boolean =>
	index > 0 && group.kind === 'entry' && group.entry.kind === 'user';

/**
 * Ids of the assistant messages that are answers rather than narration: those
 * with no tool call after them before the next user message. Text the agent
 * writes and then follows with more tool calls ("Let me check the invoices") is
 * part of the work, not the reply.
 *
 * While a turn streams, the newest message is only a candidate: a tool call may
 * still follow it. See `isBrief` for how that is kept from flickering.
 */
const answerIds = (entries: AgentTranscriptEntry[]): Set<string> => {
	const ids = new Set<string>();
	let toolFollows = false;
	for (let index = entries.length - 1; index >= 0; index -= 1) {
		const entry = entries[index];
		if (entry.kind === 'user') {
			toolFollows = false;
		} else if (entry.kind === 'tool') {
			toolFollows = true;
		} else if (entry.kind === 'assistant' && !toolFollows) {
			ids.add(entry.id);
		}
	}
	return ids;
};

/**
 * Folds everything the agent did before answering — reasoning, narration and
 * every round of tool calls — into one activity group, so a turn reads as one
 * collapsed line followed by the answer. Approvals, files and notices stay out
 * in the open: they either need the reader or are a result in their own right.
 */
/**
 * Flags each turn's last activity row, and whether the turn ended on an error:
 * its last tool call failed and no answer came after. A failure the agent
 * recovered from — by retrying, or by answering anyway — is left to the failed
 * row inside the trace.
 */
const markTurnEnds = (groups: TranscriptGroup[]) => {
	let answered = false;
	let lastActivity: Extract<TranscriptGroup, { kind: 'activity' }> | null = null;
	// The turn's most recent tool call, wherever it sits — a long remark can
	// split a turn's work across two rows, and only the last row gets a summary.
	let lastTool: ToolEntry | null = null;
	let toolCount = 0;
	let retried = false;
	const closeTurn = () => {
		if (lastActivity) {
			lastActivity.lastInTurn = true;
			lastActivity.failed = !answered && !!lastTool && lastTool.isError;
			lastActivity.turnStepCount = toolCount;
			lastActivity.turnRetried = retried;
		}
		answered = false;
		lastActivity = null;
		lastTool = null;
		toolCount = 0;
		retried = false;
	};
	for (let index = groups.length - 1; index >= 0; index -= 1) {
		const group = groups[index];
		if (group.kind === 'entry' && group.entry.kind === 'user') {
			closeTurn();
		} else if (group.kind === 'entry' && group.entry.kind === 'assistant' && group.entry.text) {
			answered = answered || !lastActivity;
		} else if (group.kind === 'activity') {
			lastActivity = lastActivity || group;
			retried = retried || group.steps.some((step) => step.kind === 'restart');
			const toolSteps = group.steps.filter((step) => step.kind === 'tool');
			toolCount += toolSteps.length;
			if (!lastTool) {
				const lastToolStep = toolSteps[toolSteps.length - 1];
				lastTool = lastToolStep && lastToolStep.kind === 'tool' ? lastToolStep.tool : null;
			}
		}
	}
	closeTurn();
};

/** Before the first user message — a conversation resumed mid-turn. */
const OPENING_TURN_ID = 'opening';

/** Past this, a single line of text is content rather than a lead-in. */
const BRIEF_TEXT_MAX_LENGTH = 160;

/**
 * A one-line remark — "Let me check the open invoices". Only brief text folds
 * into the activity row.
 *
 * Whether streaming text is the answer or a lead-in to a tool call is unknown
 * until the next event, so rendering it as the answer and folding it away when
 * a tool call started made the conversation jump on every step. Instead, brief
 * text in flight is shown as the live row's status line — where it stays if a
 * tool call follows — and is only promoted into the conversation once it grows
 * past a line or the turn ends on it. Longer text is real content, so it keeps
 * its place even when a tool call follows, and nothing it said is hidden.
 */
const isBrief = (text: string): boolean => {
	const trimmed = text.trim();
	return trimmed.indexOf('\n') === -1 && trimmed.length <= BRIEF_TEXT_MAX_LENGTH;
};

/** The turn a list of entries is currently on: its last user message. */
const currentTurnIdFor = (entries: AgentTranscriptEntry[]): string =>
	entries.reduce((latest, entry) => (entry.kind === 'user' ? entry.id : latest), OPENING_TURN_ID);

/**
 * @param liveTurnId The turn still streaming, or null while idle — its answer
 * may yet turn out to be a lead-in.
 */
const groupEntries = (
	entries: AgentTranscriptEntry[],
	liveTurnId: string | null
): TranscriptGroup[] => {
	const answers = answerIds(entries);
	const groups: TranscriptGroup[] = [];
	let turnId = OPENING_TURN_ID;
	// The row this turn's steps are collecting into. Approvals, files and notices
	// sit between steps without splitting it — a turn that paused for an approval
	// is still one piece of work — but a user message or a long remark does:
	// steps added above text that came before them would read out of order.
	let openActivity: Extract<TranscriptGroup, { kind: 'activity' }> | null = null;
	const addStep = (step: OscarActivityStep) => {
		if (openActivity) {
			openActivity.steps.push(step);
			return;
		}
		openActivity = {
			kind: 'activity',
			id: step.id,
			turnId,
			steps: [step],
			lastInTurn: false,
			failed: false,
			turnStepCount: 0,
			turnRetried: false,
		};
		groups.push(openActivity);
	};
	// Inside a run of entries from an attempt the harness abandoned. The retry's
	// first entry closes it with a `restart` step, so the trace shows where the
	// first pass ended and the second began.
	let inFirstPass = false;
	const trackFirstPass = (entry: { id: string; superseded?: boolean }) => {
		if (entry.superseded) {
			inFirstPass = true;
		} else if (inFirstPass) {
			inFirstPass = false;
			addStep({ kind: 'restart', id: `${entry.id}:restart` });
		}
	};
	entries.forEach((entry) => {
		if (entry.kind === 'user') {
			turnId = entry.id;
			openActivity = null;
			inFirstPass = false;
		}
		const last = groups[groups.length - 1];
		if (entry.kind === 'tool') {
			trackFirstPass(entry);
			addStep({ kind: 'tool', id: entry.id, tool: entry });
			return;
		}
		if (entry.kind === 'assistant') {
			trackFirstPass(entry);
			if (entry.thinking) {
				addStep({ kind: 'reasoning', id: `${entry.id}:reasoning`, text: entry.thinking });
			}
			if (!entry.text) {
				return;
			}
			// What an abandoned attempt wrote stays in the trace, folded away with
			// the rest of the work: shown in the open it competes with the retry's
			// answer, and as a note it could read as the retry's live status.
			if (entry.superseded) {
				addStep({ kind: 'discarded', id: `${entry.id}:discarded`, text: entry.text });
				return;
			}
			const settledAnswer = answers.has(entry.id) && turnId !== liveTurnId;
			if (settledAnswer || !isBrief(entry.text)) {
				groups.push({ kind: 'entry', id: entry.id, entry });
				openActivity = null;
				return;
			}
			addStep({ kind: 'note', id: `${entry.id}:note`, text: entry.text });
			return;
		}
		// One question can queue a write per record, and stacked vertically those
		// bury everything below them.
		if (entry.kind === 'approval') {
			if (last && last.kind === 'approvals') {
				last.approvals.push(entry);
				return;
			}
			groups.push({ kind: 'approvals', id: entry.id, approvals: [entry] });
			return;
		}
		groups.push({ kind: 'entry', id: entry.id, entry });
	});
	markTurnEnds(groups);
	return groups;
};

interface OscarTranscriptProps {
	/** Straight from `useAgentChat().entries`. */
	entries: AgentTranscriptEntry[];
	/** `useAgentChat().busy` — drives the thinking indicator. */
	busy?: boolean;
	/** `useAgentChat().turnStartedAt` — drives the elapsed timer. */
	turnStartedAt?: number;
	/** Letter shown in the avatar beside the user's prompt. */
	userInitial?: string;
	onResolveApproval?: (approvalId: string, approved: boolean) => void | Promise<boolean>;
	/** `useAgentChat().reconcileAction` — answers a lost write's review card. */
	onReconcileAction?: (
		actionId: string,
		outcome: AgentActionOutcome,
		evidence: string
	) => void | Promise<boolean>;
	/** `downloadAgentFile`, injected by the container. */
	onDownloadFile?: (url: string) => Promise<Blob>;
	/** `useAgentChat().unsentMessages` — puts a retry under each failed message. */
	unsentMessages?: AgentUnsentMessages;
	/** `useAgentChat().retrySend`. Omit while the connection strip owns the retry. */
	onRetrySend?: (messageId: string) => void;
	/**
	 * Max width of the reading column. The drawer caps it so lines stay
	 * comfortable; fullscreen passes `OSCAR_FULL_MEASURE` and takes the room.
	 */
	measure?: string;
	className?: string;
}

/**
 * Figma node 14998:125498 — the conversation pane. The user's prompt is a grey
 * card with an avatar; the agent's reply is plain paragraphs, so long answers
 * read as prose rather than chat bubbles.
 *
 * Only the `user` and `assistant` rows are designed. The stream also emits tool
 * calls, approvals, files and notices; those are rendered plainly from existing
 * tokens and are flagged as design gaps in DESIGN_TOKEN_GAPS.md.
 *
 * Message bodies are the one place here that genuinely uses `<p>` — an agent
 * answer is real prose. `mb-0` is needed because `src/index.less` gives every
 * `p` a 1em bottom margin app-wide; spacing comes from the parent's `gap`.
 */
export const OscarTranscript = ({
	entries,
	busy,
	turnStartedAt = 0,
	userInitial = '?',
	onResolveApproval,
	onReconcileAction,
	onDownloadFile,
	unsentMessages,
	onRetrySend,
	measure = OSCAR_READING_MEASURE,
	className,
}: OscarTranscriptProps) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const pinnedToBottomRef = useRef(true);
	const turn = useOscarTurnStatus(!!busy, entries, turnStartedAt);
	// How long each turn took, for the settled summary. Entries carry no
	// timestamps, so this is only known for turns watched live; history says how
	// many steps and leaves the time out. Written during render because the
	// clock's last tick is the value wanted, and rewriting it is idempotent.
	const turnSecondsRef = useRef<{ [turnId: string]: number }>({});
	const currentTurnId = currentTurnIdFor(entries);
	const groups = groupEntries(entries, busy ? currentTurnId : null);
	if (busy && turn.seconds > 0) {
		turnSecondsRef.current[currentTurnId] = turn.seconds;
	}
	const hasLiveRow = groups.some(
		(group) => group.kind === 'activity' && group.turnId === currentTurnId && group.lastInTurn
	);
	// Between sending and the first reasoning, remark or tool call there is no
	// activity row to carry the indicator yet, so start an empty one where it
	// will appear. A long answer streaming on its own needs none — the text is
	// its own progress.
	if (busy && turn.label && !hasLiveRow) {
		groups.push({
			kind: 'activity',
			id: `live:${currentTurnId}`,
			turnId: currentTurnId,
			steps: [],
			lastInTurn: true,
			failed: false,
			turnStepCount: 0,
			turnRetried: false,
		});
	}

	// Answers stream in token by token, so the view has to follow — but only if
	// the reader is already at the bottom. Yanking someone who scrolled up to
	// re-read something is worse than not following at all.
	const handleScroll = () => {
		const el = scrollRef.current;
		if (!el) {
			return;
		}
		pinnedToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
	};

	useEffect(() => {
		const el = scrollRef.current;
		if (el && pinnedToBottomRef.current) {
			el.scrollTop = el.scrollHeight;
		}
	}, [entries, turn.label, turn.seconds, unsentMessages]);

	return (
		<div
			ref={scrollRef}
			onScroll={handleScroll}
			className={cn('flex min-h-0 w-full flex-1 flex-col overflow-y-auto py-l', className)}
		>
			{/* The scroller stays full width so the scrollbar sits at the panel's
			    edge; this inner column carries the reading measure and centres
			    itself, and full-bleed rows bleed to it rather than to the viewport. */}
			<div className={cn('mx-auto flex w-full flex-col gap-s', measure)}>
				{/* The transcript renders model-authored content — markdown, tables,
				    tool payloads — so a render throw here is a real possibility rather
				    than a theoretical one. Contained to the transcript: the header, the
				    composer and New chat stay usable, and in the dock's case a throw no
				    longer takes the app's top bar down with it on every page. Per-row
				    isolation would be better still and is worth doing; this is the
				    version that does not require reshaping the loop below. */}
				<ErrorBoundary
					fallback={
						<div className={cn('bodyregular text-character-secondary', OSCAR_CONTENT_INSET)}>
							This conversation could not be displayed. Starting a new chat should clear it.
						</div>
					}
				>
					{groups.map((group, index) => {
						if (group.kind === 'activity') {
							const isLive = !!busy && group.lastInTurn && group.turnId === currentTurnId;
							return (
								<div key={group.id} className={cn('flex w-full shrink-0', OSCAR_CONTENT_INSET)}>
									<OscarActivityTrace
										steps={group.steps}
										live={
											isLive
												? {
														// No label while the answer streams — the text is the
														// progress — but the turn is not over, so the row says so.
														label: turn.label || 'answering',
														seconds: turn.seconds,
														awaitingApproval: turn.awaitingApproval,
												  }
												: null
										}
										durationSeconds={
											group.lastInTurn ? turnSecondsRef.current[group.turnId] : undefined
										}
										stepCount={group.lastInTurn ? group.turnStepCount : undefined}
										failed={!isLive && group.failed}
										retried={group.lastInTurn && group.turnRetried}
									/>
								</div>
							);
						}
						if (group.kind === 'approvals') {
							return (
								// No inset here: the queue bleeds to both edges of the column and
								// pads its own header, so cards can scroll past the gutter.
								<div key={group.id} className="flex w-full min-w-0 shrink-0">
									<OscarApprovalQueue approvals={group.approvals} onResolve={onResolveApproval} />
								</div>
							);
						}
						const entry = group.entry;
						switch (entry.kind) {
							case 'user': {
								const unsent = unsentMessages ? unsentMessages[entry.id] : undefined;
								return (
									<React.Fragment key={entry.id}>
										<div
											className={cn(
												'flex w-full shrink-0 items-start gap-xs',
												OSCAR_CONTENT_INSET,
												startsTurn(group, index) && 'mt-l'
											)}
										>
											<div className="flex flex-1 flex-col justify-center gap-2xs rounded bg-grey-3 px-m py-xs">
												{/* The composer takes Shift+Enter for a newline and sends it, so the
										    transcript has to show it: plain HTML collapsed a pasted list
										    into one line. */}
												<p className="bodyregular mb-0 whitespace-pre-wrap break-words text-character-primary">
													{entry.text}
												</p>
											</div>
											<span
												aria-hidden="true"
												className="bodyregular flex size-8 shrink-0 items-center justify-center rounded-full border border-solid border-white bg-[#ff7a45] text-center text-white"
											>
												{userInitial}
											</span>
										</div>
										{unsent ? (
											<OscarUnsentMessage
												reason={unsent.reason}
												onRetry={onRetrySend ? () => onRetrySend(entry.id) : undefined}
												className={OSCAR_CONTENT_INSET}
											/>
										) : null}
									</React.Fragment>
								);
							}
							case 'assistant':
								// An answer with no text renders nothing: its reasoning is already in
								// the activity line above, and while it streams the trailing indicator
								// is saying what is happening — two spinners for one turn is worse
								// than none.
								if (!entry.text) {
									return null;
								}
								return (
									<div key={entry.id} className={cn('flex w-full shrink-0', OSCAR_CONTENT_INSET)}>
										<OscarMarkdown>{entry.text}</OscarMarkdown>
									</div>
								);
							case 'file':
								return (
									<div key={entry.id} className={cn('flex w-full shrink-0', OSCAR_CONTENT_INSET)}>
										<OscarFileCard
											name={entry.name}
											size={entry.size}
											url={entry.url}
											onDownload={onDownloadFile}
										/>
									</div>
								);
							case 'team_request':
								return (
									<div key={entry.id} className={cn('flex w-full shrink-0', OSCAR_CONTENT_INSET)}>
										<OscarTeamRequestSentCard request={entry.request} />
									</div>
								);
							case 'action_review':
								return (
									<div key={entry.id} className={cn('flex w-full shrink-0', OSCAR_CONTENT_INSET)}>
										<OscarActionReviewCard
											toolName={entry.toolName}
											createdAt={entry.createdAt}
											state={entry.state}
											onReconcile={
												onReconcileAction
													? (outcome, evidence) => onReconcileAction(entry.id, outcome, evidence)
													: undefined
											}
										/>
									</div>
								);
							case 'notice':
								return (
									<div key={entry.id} className={cn('flex w-full shrink-0', OSCAR_CONTENT_INSET)}>
										<span
											className={cn(
												'footnoteregular',
												entry.severity === 'error'
													? 'text-character-danger'
													: 'text-character-secondary'
											)}
										>
											{entry.severity === 'error' ? humanizeAgentError(entry.text) : entry.text}
										</span>
									</div>
								);
							default:
								return null;
						}
					})}
				</ErrorBoundary>
			</div>
		</div>
	);
};

export default OscarTranscript;
